/**
 * vwap — Session-anchored Volume-Weighted Average Price.
 *
 * Pure math, no side effects. Two consumers:
 *
 *   1. The TradeChart, which renders VWAP as a Lightweight Charts line
 *      series alongside the EMA overlays.
 *
 *   2. The bar-analysis pipeline (maeMfe.js), which snapshots VWAP at the
 *      trade's entry timestamp so the Coach can reason about
 *      price-relative-to-VWAP position — "you went long below VWAP",
 *      "VWAP reclaim/bounce", etc.
 *
 * Session anchoring
 * -----------------
 * Futures traders care about VWAP from the session open, not the calendar
 * day. The futures session runs 6pm ET previous day → 5pm ET today, so
 * VWAP must reset at the 6pm ET boundary. We delegate trading-day labeling
 * to `timeService.getTradingDay`, which already handles the boundary +
 * cross-timezone correctness.
 *
 * Algorithm
 * ---------
 * For each bar in order:
 *   typicalPrice = (high + low + close) / 3   (HLC/3 — standard)
 *   sumPV    += typicalPrice * volume
 *   sumV     += volume
 *   VWAP[i]   = sumPV / sumV   (or null if sumV = 0)
 *
 * Whenever a bar's trading day changes from the previous bar's, the running
 * sums reset. This means a multi-day bar array (e.g. a monthly rolling
 * file) produces independent VWAP curves per session — no leakage across
 * the 6pm ET boundary. Single-day inputs (the common case via the
 * NinjaTraderBarSource slicer) work correctly too with no special handling.
 *
 * NaN handling
 * ------------
 * Bars with non-finite high/low/close OR with non-positive volume produce
 * a null point and don't contribute to the running sums. This matches the
 * EMA module's NaN-tolerant convention.
 */

import { getTradingDay } from '../timeService.js';

/**
 * Compute session-anchored VWAP for a bar series.
 *
 * @param {Array<{time:number, high:number, low:number, close:number, volume:number}>} bars
 *        Sorted ascending by `time` (Unix seconds). Same convention as the
 *        rest of the bar pipeline.
 * @returns {Array<{time:number, value:number|null}>}  Same length as input.
 */
export const computeVWAP = (bars) => {
  if (!Array.isArray(bars) || bars.length === 0) return [];

  const out = new Array(bars.length);
  let sumPV = 0;
  let sumV = 0;
  let currentDay = null;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const t = Number(b?.time);
    if (!Number.isFinite(t)) {
      out[i] = { time: t, value: null };
      continue;
    }

    // Reset on session boundary (6pm ET trading-day rollover).
    const day = getTradingDay(t * 1000);
    if (day && day !== currentDay) {
      currentDay = day;
      sumPV = 0;
      sumV = 0;
    }

    const hi = Number(b.high);
    const lo = Number(b.low);
    const cl = Number(b.close);
    const vol = Number(b.volume);
    if (!Number.isFinite(hi) || !Number.isFinite(lo) || !Number.isFinite(cl) || !Number.isFinite(vol) || vol <= 0) {
      // Don't poison sums; carry forward the prior VWAP if we have one,
      // otherwise null (nothing to anchor on yet).
      out[i] = { time: t, value: sumV > 0 ? sumPV / sumV : null };
      continue;
    }

    const typicalPrice = (hi + lo + cl) / 3;
    sumPV += typicalPrice * vol;
    sumV  += vol;
    out[i] = { time: t, value: sumV > 0 ? sumPV / sumV : null };
  }

  return out;
};

/**
 * Look up the VWAP value at (or just before) a target timestamp.
 *
 * Mirrors `emaAtTime` in shape so the bar-analysis pipeline can use it
 * symmetrically. Returns null when the series is empty, all-null, or the
 * target precedes any valid VWAP point.
 *
 * @param {Array<{time:number, value:number|null}>} series
 * @param {number} targetTime  Unix seconds.
 * @returns {number|null}
 */
export const vwapAtTime = (series, targetTime) => {
  if (!Array.isArray(series) || series.length === 0) return null;
  if (!Number.isFinite(targetTime)) return null;
  let lastValue = null;
  for (const point of series) {
    if (!point) continue;
    if (point.time > targetTime) break;
    if (point.value !== null && Number.isFinite(point.value)) {
      lastValue = point.value;
    }
  }
  return lastValue;
};
